import classNames from "classnames";
import * as React from "react";

import styles from "./FactList.module.css";

export interface FactListProps extends React.ComponentPropsWithoutRef<"dl"> {
  /**
   * `columns` (default) sets each label beside its value, with the values
   * lined up; `stacked` sets it above, for narrow panels.
   */
  layout?: "columns" | "stacked";
  /** Rules a line between items. */
  divided?: boolean;
}

function FactListRoot({
  layout = "columns",
  divided = false,
  className,
  ...props
}: FactListProps) {
  return (
    <dl
      {...props}
      className={classNames(styles.list, className)}
      data-layout={layout}
      data-divided={divided || undefined}
    />
  );
}

export interface FactListItemProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "children"> {
  label: React.ReactNode;
  children: React.ReactNode;
  /** Sets the value in monospace, for ids. */
  mono?: boolean;
  /** Styles the value, e.g. to keep its line breaks. */
  valueClassName?: string;
}

function FactListItem({
  label,
  children,
  mono = false,
  valueClassName,
  className,
  ...props
}: FactListItemProps) {
  return (
    <div {...props} className={classNames(styles.item, className)}>
      <dt className={styles.label}>{label}</dt>
      <dd
        className={classNames(
          styles.value,
          { [styles.mono]: mono },
          valueClassName,
        )}
      >
        {children}
      </dd>
    </div>
  );
}

/**
 * Read-only facts about a record, one label and value to a row.
 *
 * @example
 * ```tsx
 * <FactList>
 *   <FactList.Item label="Rule ID" mono>{rule.rule_id}</FactList.Item>
 *   <FactList.Item label="Created">{created}</FactList.Item>
 * </FactList>
 * ```
 */
export const FactList = Object.assign(FactListRoot, { Item: FactListItem });
